import type { Direction } from "../types/common";

// Column letter ranges for the YINSH board.
// 85 playing intersections in an asymmetric hexagonal lattice.
// col index: a=0, b=1, ..., k=10
// Per design handoff: docs/task-log/20260428-design_handoff_board_games/games/yinsh.md
const COL_ROW_RANGES: readonly [number, number][] = [
  [2, 5], // a — 4 positions
  [1, 7], // b — 7
  [1, 8], // c — 8
  [1, 9], // d — 9
  [1, 10], // e — 10
  [2, 10], // f — 9
  [2, 11], // g — 10
  [3, 11], // h — 9
  [4, 11], // i — 8
  [5, 11], // j — 7
  [7, 10], // k — 4
];

function posKey(col: number, row: number): string {
  return `${String.fromCharCode(97 + col)}${row}`;
}

// Build VALID_POSITIONS set
function buildValidPositions(): ReadonlySet<string> {
  const s = new Set<string>();
  for (let col = 0; col < 11; col++) {
    const [minRow, maxRow] = COL_ROW_RANGES[col];
    for (let row = minRow; row <= maxRow; row++) {
      s.add(posKey(col, row));
    }
  }
  return s;
}

export const VALID_POSITIONS: ReadonlySet<string> = buildValidPositions();

// YINSH uses an axial-coord triangular lattice. (col, row) IS the axial
// coordinate. The 3 line axes through any point are (±1,0), (0,±1), (±1,±1).
//
// In the canonical visual projection (see games-design-system.html lines
// 2030–2034 and apps/board-game-{playground,client}/src/games/yinsh/utils/
// board-geometry.ts), pointXY(col, row) places points such that:
//   axis  (+1,  0): horizontal east
//   axis  ( 0, +1): NW (upper-left at 60°)
//   axis  (+1, +1): NE (upper-right at 60°)
// and their opposites for W, SE, SW. These are the 6 directions a YINSH
// ring can move in a straight line.
//
// The historical "N/S/NE/SE/SW/NW" enum names are kept for compatibility but
// re-mapped to these axial vectors (no parity-dependent zig-zag).
const DIR_VECTORS: Record<Direction, readonly [number, number]> = {
  N: [0, 1], // axis 1 +  (visually NW)
  S: [0, -1], // axis 1 −  (visually SE)
  NE: [1, 1], // axis 2 +  (visually NE)
  SW: [-1, -1], // axis 2 −  (visually SW)
  SE: [1, 0], // axis 3 +  (visually horizontal east)
  NW: [-1, 0], // axis 3 −  (visually horizontal west)
};

// Build neighbor table — pure axial integer arithmetic, no parity offsets.
function buildNeighbors(): Readonly<
  Record<string, Partial<Record<Direction, string>>>
> {
  const result: Record<string, Partial<Record<Direction, string>>> = {};
  for (const pos of VALID_POSITIONS) {
    const col = pos.charCodeAt(0) - 97;
    const row = Number.parseInt(pos.slice(1), 10);
    const neighbors: Partial<Record<Direction, string>> = {};
    for (const [dir, [dc, dr]] of Object.entries(DIR_VECTORS) as [
      Direction,
      readonly [number, number],
    ][]) {
      const nc = col + dc;
      const nr = row + dr;
      if (nc >= 0 && nc < 11) {
        const nkey = posKey(nc, nr);
        if (VALID_POSITIONS.has(nkey)) {
          neighbors[dir] = nkey;
        }
      }
    }
    result[pos] = neighbors;
  }
  return result;
}

export const NEIGHBORS: Readonly<
  Record<string, Partial<Record<Direction, string>>>
> = buildNeighbors();

const DIRECTION_PAIRS: readonly [Direction, Direction][] = [
  ["N", "S"],
  ["NE", "SW"],
  ["SE", "NW"],
];

// Build all lines of length >= 5 (for row detection)
function buildAllLines(): readonly (readonly string[])[] {
  const lines: string[][] = [];
  const seen = new Set<string>();

  for (const pos of VALID_POSITIONS) {
    for (const [fwd] of DIRECTION_PAIRS) {
      // Walk forward to find the start of a line (walk backward to find beginning)
      const bwd =
        DIRECTION_PAIRS.find(([f]) => f === fwd)?.[1] ?? ("S" as Direction);
      // Find the start of this line in fwd direction
      let start = pos;
      let prev = NEIGHBORS[start]?.[bwd];
      while (prev !== undefined) {
        start = prev;
        prev = NEIGHBORS[start]?.[bwd];
      }

      // Build the full line from start in fwd direction
      const line: string[] = [];
      let cur: string | undefined = start;
      while (cur !== undefined) {
        line.push(cur);
        cur = NEIGHBORS[cur]?.[fwd];
      }

      if (line.length >= 5) {
        const key = line.join(",");
        if (!seen.has(key)) {
          seen.add(key);
          lines.push(line);
        }
      }
    }
  }
  return lines;
}

export const ALL_LINES: readonly (readonly string[])[] = buildAllLines();

// SVG coordinates for equilateral-triangle hexagonal grid.
//
// Cube coords: q = col, rCube = row - 1 - floor(col/2)
// SVG mapping (N-S lines vertical, NE/NW at 30° from horizontal):
//   x = CENTER_X + CELL_SIZE * (√3/2) * (q - 5)
//   y = CENTER_Y - CELL_SIZE * (rCube + q/2 - 5.5)
//
// Board fits in viewBox "0 0 500 600":
//   x: ~51–449  (398 px wide, 51 px margin each side)
//   y: ~59–542  (483 px tall, ~59 px top / 58 px bottom)
const CELL_SIZE = 46;
const SQRT3_OVER_2 = Math.sqrt(3) / 2;
const CENTER_X = 250;
// CENTER_Y chosen so the rendered board (vertical extents from G11 at the top
// to E1 at the bottom) is centred in a 600 px-tall viewBox. Board height is
// CELL_SIZE * 10 = 460 px → 70 px top/bottom margin at CENTER_Y = 277.
const CENTER_Y = 277;

function buildSvgCoords(): Readonly<
  Record<string, { readonly x: number; readonly y: number }>
> {
  const result: Record<string, { x: number; y: number }> = {};

  for (let col = 0; col < 11; col++) {
    const [minRow, maxRow] = COL_ROW_RANGES[col];
    const q = col;
    const x = CENTER_X + CELL_SIZE * SQRT3_OVER_2 * (q - 5);

    for (let row = minRow; row <= maxRow; row++) {
      const rCube = row - 1 - Math.floor(col / 2);
      const y = CENTER_Y - CELL_SIZE * (rCube + q / 2 - 5.5);
      result[posKey(col, row)] = { x, y };
    }
  }
  return result;
}

export const SVG_COORDS: Readonly<
  Record<string, { readonly x: number; readonly y: number }>
> = buildSvgCoords();

// Perimeter labels for the YINSH board.
//
// Each column (A–K) gets its letter placed just outside the bottom-most
// playable position of that column. Each row (1–11) gets its number placed
// just outside the leftmost playable position of that row. Offsets follow
// the hex perimeter direction so labels read along the outer edge.
//
// In the 85-position layout:
// Bottom-most position per col:
//   a2, b1, c1, d1, e1, f2, g2, h3, i4, j5, k6
// Leftmost position per row:
//   row1=b1, row2=a2, row3=a3, row4=a4, row5=a5,
//   row6=b6, row7=b7, row8=c8, row9=d9, row10=e10, row11=g11

interface LabelPos {
  readonly label: string;
  readonly x: number;
  readonly y: number;
}

function buildLetterLabels(): readonly LabelPos[] {
  // [bottomCol, bottomRow, dx, dy] per letter A..K
  const spec: readonly [number, number, number, number][] = [
    [0, 2, -8, 18], // A — col a, row 2 (SW corner cell)
    [1, 1, 0, 22], // B — col b, row 1 (along bottom edge)
    [2, 1, 0, 22], // C
    [3, 1, 0, 22], // D
    [4, 1, 0, 22], // E
    [5, 2, 16, 20], // F — col f, row 2 (SE diagonal)
    [6, 2, 16, 20], // G
    [7, 3, 16, 20], // H
    [8, 4, 16, 20], // I
    [9, 5, 16, 20], // J
    [10, 7, 16, 20], // K — col k, row 7 (SE corner cell)
  ];
  return spec.map(([col, row, dx, dy], i) => {
    const anchor = SVG_COORDS[posKey(col, row)];
    return {
      label: String.fromCharCode(65 + i),
      x: anchor.x + dx,
      y: anchor.y + dy,
    };
  });
}

function buildNumberLabels(): readonly LabelPos[] {
  // [leftCol, row, dx, dy] per number 1..11
  const spec: readonly [number, number, number, number][] = [
    [1, 1, -16, 20], // 1 — leftmost on row 1 is b1 (SW corner area)
    [0, 2, -22, 0], // 2 — row 2 leftmost is a2
    [0, 3, -22, 0], // 3 — row 3 leftmost is a3
    [0, 4, -22, 0], // 4
    [0, 5, -22, 0], // 5
    [1, 6, -16, -20], // 6 — row 6 leftmost is b6
    [1, 7, -16, -20], // 7 — row 7 leftmost is b7 (B now extends to row 7)
    [2, 8, -16, -20], // 8 — row 8 leftmost is c8
    [3, 9, -16, -20], // 9 — row 9 leftmost is d9
    [4, 10, -16, -20], // 10 — row 10 leftmost is e10
    [6, 11, -16, -20], // 11 — row 11 leftmost is g11
  ];
  return spec.map(([col, row, dx, dy], i) => {
    const anchor = SVG_COORDS[posKey(col, row)];
    return {
      label: String(i + 1),
      x: anchor.x + dx,
      y: anchor.y + dy,
    };
  });
}

export const LETTER_LABELS: readonly LabelPos[] = buildLetterLabels();
export const NUMBER_LABELS: readonly LabelPos[] = buildNumberLabels();
